import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Read } from 'services/financing-proposals';
import { Icon, StatusContent } from 'ui/styled';
import { exposeStrapiError, marcadorDiferente, selectColors } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function FinancingDeskUnderAnalysis() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, filterExpression, setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);

  // Atualiza o tempo de exibição a cada 60s
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Colunas da tabela
  const columns = useMemo(() => {
    const defaultColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
        renderCell: ({ row }) => {
          // Verifica se foi analisado pela curadoria humana ou pelo bot
          const isAnalisadoCuradoria = row?.curadoria_analise === 'Analisado';
          const isAnalisadoBot = row?.curadoria_analise_bot === 'Analisado';
          const id = row?.id;
          if (isAnalisadoCuradoria || isAnalisadoBot) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.date),
      },
      {
        title: 'Tempo',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      { title: 'Proposta', ref: 'cod_proposta_externa' },
      ...(user?.administrador ||
      (user?.distribuidores && user.distribuidores.length >= 1)
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor', ref: 'value' },
      {
        title: 'Status',
        ref: 'status',
        renderCell: ({ row }) => (
          <StatusContent color={selectColors(row?.status)}>
            {row?.status}
          </StatusContent>
        ),
      },
      ...(user?.administrador
        ? [
            {
              title: 'Observações',
              renderCell: ({ row }) => (
                <Button
                  onClick={() => {
                    setModal({
                      type: 'observacao',
                      dados: row,
                      origem: 'Em Análise',
                      reload: () => init(),
                    });
                  }}
                  color="botao"
                  nospace
                  mbutton
                  small
                >
                  Adicionar
                </Button>
              ),
            },
          ]
        : []),
      {
        title: '',
        ref: '',
        renderCell: ({ row }) => (
          <Icon
            icon="arrow-right"
            pointer
            onClick={() =>
              navigate(`financing-table/${row?.id}/under-analysis`)
            }
          />
        ),
      },
    ];
    return defaultColumns.filter((f) => f);
  }, [user, navigate, setModal]);

  const rows = useMemo(() => {
    return registers?.map((item) => ({
      id: item?.id,
      date: item?.data_em_analise,
      formatedDate: formatadores.TIMESTAMP_TO_BR(item?.data_em_analise),
      cod_proposta_externa: normalizeName(item?.cod_proposta_externa),
      distributor: normalizeName(item?.distribuidor?.trade_name),
      integrator: normalizeName(item?.integrador?.nome_fantasia),
      seller: normalizeName(item?.informacoes_projeto?.nome_vendedor),
      telephone: item?.informacoes_cliente?.telefone_celular,
      customer: normalizeName(item?.informacoes_cliente?.nome_completo),

      value: parseCurrency(item?.informacoes_projeto?.custo_total_projeto),
      status:
        item?.status === 'em_analise'
          ? 'Em Análise'
          : normalizeName(item?.status),
      espelho: item?.espelho_pdf?.data?.[0]?.attributes?.url ?? null,
      curadoria_analise: item?.curadoria_analise,
      curadoria_analise_bot: item?.curadoria_analise_bot,
      cnpj_integrador: item?.integrador?.cnpj,
      cnpj_integrador_clean: item?.integrador?.cnpj.replace(/\D/g, ''),
      cpf_cnpj: item?.cliente?.cpf || item?.cliente?.cnpj,
      cpf_cnpj_clean: item?.cliente?.cpf
        ? item?.cliente?.cpf.replace(/\D/g, '')
        : item?.cliente?.cnpj.replace(/\D/g, ''),
    }));
  }, [registers]);

  const init = useCallback(async () => {
    setLoading(true);
    const result = await Read('?filters[status]=em_analise&populate=*');
    setLoading(false);
    if (result && !exposeStrapiError(result)) {
      const sorted = result?.results?.sort((a, b) => b.id - a.id);
      const next = sorted?.map((m) => ({
        ...m,
        accepted: m?.propostas_banco?.find((f) => f.aceito),
      }));
      setRegisters(next);
    }
  }, []);

  useEffect(() => init(), [tick, init]);

  return (
    <ContainerAuthenticated>
      <TableWrapper title="Em Análise" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
